/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiIcon } from '../icon';
import { EuiSideNavItem } from './side_nav_item';
export class EuiSideNav extends Component {
  static defaultProps = {
    items: []
  };
  isItemOpen = item => {
    // The developer can force the item to be open.
    if (item.forceOpen) {
      return true;
    } // Of course a selected item is open.


    if (item.isSelected) {
      return true;
    } // The item has to be open if it has a child that's open.


    if (item.items) {
      return item.items.some(this.isItemOpen);
    }

    return false;
  };
  renderTree = (items, depth = 0) => {
    const {
      renderItem
    } = this.props;
    return items.map(item => {
      const {
        id,
        name,
        isSelected,
        items: childItems,
        icon,
        onClick,
        href,
        forceOpen,
        ...rest
      } = item; // Root items are always open.

      const isOpen = depth === 0 ? true : this.isItemOpen(item);
      let renderedItems;

      if (childItems) {
        renderedItems = this.renderTree(childItems, depth + 1);
      }

      return <EuiSideNavItem isOpen={isOpen} isSelected={isSelected} isParent={!!childItems} icon={icon} onClick={onClick} href={href} items={renderedItems} key={id} depth={depth} renderItem={renderItem} {...rest}>
          {name}
        </EuiSideNavItem>;
    });
  };

  render() {
    const {
      className,
      items,
      toggleOpenOnMobile,
      isOpenOnMobile,
      mobileTitle,
      // Extract this one out so it isn't passed to <nav>
      renderItem,
      ...rest
    } = this.props;
    const classes = classNames('euiSideNav', className, {
      'euiSideNav-isOpenMobile': isOpenOnMobile
    });
    const nav = this.renderTree(items);
    return <nav className={classes} {...rest}>
        {
        /* Hidden from view, except in mobile */
      }
        <button type="button" className="euiSideNav__mobileToggle euiLink" onClick={toggleOpenOnMobile}>
          <span className="euiSideNav__mobileWrap">
            <span className="euiSideNav__mobileTitle">{mobileTitle}</span>

            <EuiIcon className="euiSideNav__mobileIcon" type="apps" size="m" aria-hidden="true" />
          </span>
        </button>

        {
        /* Hidden from view in mobile, but toggled from the button above */
      }
        <div className="euiSideNav__content">{nav}</div>
      </nav>;
  }

}
EuiSideNav.propTypes = {
  /**
       * Class names to be merged into the final `className` property.
       */
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
       * When called, toggles visibility of the navigation menu at mobile responsive widths. The callback should set the `isOpenOnMobile` prop to actually toggle navigation visibility.
       */
  toggleOpenOnMobile: PropTypes.func,

  /**
       * If `true`, the navigation menu will be open at mobile device widths. Use in conjunction with the `toggleOpenOnMobile` prop.
       */
  isOpenOnMobile: PropTypes.bool,

  /**
       * A React node to render at mobile responsive widths, representing the title of this navigation menu.
       */
  mobileTitle: PropTypes.node,

  /**
       *  An array of #EuiSideNavItem objects. Lists navigation menu items.
       */
  items: PropTypes.arrayOf(PropTypes.shape({
    /**
       * A value that is passed to React as the `key` for this item
       */
    id: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]).isRequired,

    /**
       * If set to true it will force the item to display in an "open" state at all times.
       */
    forceOpen: PropTypes.bool,

    /**
       * Is an optional string to be passed as the navigation item's `href` prop, and by default it will force rendering of the item as an `<a>`.
       */
    href: PropTypes.string,
    target: PropTypes.string,
    rel: PropTypes.string,

    /**
       * React node which will be rendered as a small icon to the left of the navigation item text.
       */
    icon: PropTypes.element,

    /**
       * If set to true it will render the item in a visible "selected" state, and will force all ancestor navigation items to render in an "open" state.
       */
    isSelected: PropTypes.bool,

    /**
       * Array containing additional item objects, representing nested children of this navigation item.
       */
    items: PropTypes.arrayOf(PropTypes.shape({
      /**
         * A value that is passed to React as the `key` for this item
         */
      id: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]).isRequired,

      /**
         * If set to true it will force the item to display in an "open" state at all times.
         */
      forceOpen: PropTypes.bool,

      /**
         * Is an optional string to be passed as the navigation item's `href` prop, and by default it will force rendering of the item as an `<a>`.
         */
      href: PropTypes.string,
      target: PropTypes.string,
      rel: PropTypes.string,

      /**
         * React node which will be rendered as a small icon to the left of the navigation item text.
         */
      icon: PropTypes.element,

      /**
         * If set to true it will render the item in a visible "selected" state, and will force all ancestor navigation items to render in an "open" state.
         */
      isSelected: PropTypes.bool,

      /**
         * Array containing additional item objects, representing nested children of this navigation item.
         */
      items: PropTypes.arrayOf(PropTypes.any.isRequired),

      /**
         * React node representing the text to render for this item (usually a string will suffice).
         */
      name: PropTypes.node.isRequired,

      /**
         * Callback function to be passed as the navigation item's `onClick` prop, and by default it will force rendering of the item as a `<button>` instead of a link.
         */
      onClick: PropTypes.func,

      /**
         * Function overriding default rendering for this navigation item — when called, it should return a React node representing a replacement navigation item.
         */
      renderItem: PropTypes.func
    }).isRequired),

    /**
       * React node representing the text to render for this item (usually a string will suffice).
       */
    name: PropTypes.node.isRequired,

    /**
       * Callback function to be passed as the navigation item's `onClick` prop, and by default it will force rendering of the item as a `<button>` instead of a link.
       */
    onClick: PropTypes.func,

    /**
       * Function overriding default rendering for this navigation item — when called, it should return a React node representing a replacement navigation item.
       */
    renderItem: PropTypes.func
  }).isRequired).isRequired,

  /**
       * Overrides default navigation menu item rendering. When called, it should return a React node representing a replacement navigation item.
       */
  renderItem: PropTypes.func
};

try {
  // @ts-ignore
  EuiSideNav.displayName = "EuiSideNav"; // @ts-ignore

  EuiSideNav.__docgenInfo = {
    "description": "",
    "displayName": "EuiSideNav",
    "props": {
      "className": {
        "defaultValue": null,
        "description": "Class names to be merged into the final `className` property.",
        "name": "className",
        "required": false,
        "type": {
          "name": "EuiSideNavProps<T>[\"className\"] | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "EuiSideNavProps<T>[\"aria-label\"] | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "EuiSideNavProps<T>[\"data-test-subj\"] | undefined"
        }
      },
      "children": {
        "defaultValue": null,
        "description": "`children` are not rendered. Use `items` to specify navigation items instead.",
        "name": "children",
        "required": false,
        "type": {
          "name": "string | number | boolean | {} | ReactElement<any, string | ((props: any) => ReactElement<any, string | ... | (new (props: any) => Component<any, any, any>)> | null) | (new (props: any) => Component<...>)> | ... 4 more ... | undefined"
        }
      },
      "toggleOpenOnMobile": {
        "defaultValue": null,
        "description": "When called, toggles visibility of the navigation menu at mobile responsive widths. The callback should set the `isOpenOnMobile` prop to actually toggle navigation visibility.",
        "name": "toggleOpenOnMobile",
        "required": false,
        "type": {
          "name": "EuiSideNavProps<T>[\"toggleOpenOnMobile\"] | undefined"
        }
      },
      "isOpenOnMobile": {
        "defaultValue": null,
        "description": "If `true`, the navigation menu will be open at mobile device widths. Use in conjunction with the `toggleOpenOnMobile` prop.",
        "name": "isOpenOnMobile",
        "required": false,
        "type": {
          "name": "EuiSideNavProps<T>[\"isOpenOnMobile\"] | undefined"
        }
      },
      "mobileTitle": {
        "defaultValue": null,
        "description": "A React node to render at mobile responsive widths, representing the title of this navigation menu.",
        "name": "mobileTitle",
        "required": false,
        "type": {
          "name": "EuiSideNavProps<T>[\"mobileTitle\"] | undefined"
        }
      },
      "items": {
        "defaultValue": {
          value: "[]"
        },
        "description": "An array of #EuiSideNavItem objects. Lists navigation menu items.",
        "name": "items",
        "required": false,
        "type": {
          "name": "EuiSideNavProps<T>[\"items\"]"
        }
      },
      "renderItem": {
        "defaultValue": null,
        "description": "Overrides default navigation menu item rendering. When called, it should return a React node representing a replacement navigation item.",
        "name": "renderItem",
        "required": false,
        "type": {
          "name": "EuiSideNavProps<T>[\"renderItem\"] | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/side_nav/side_nav.tsx#EuiSideNav"] = {
      docgenInfo: EuiSideNav.__docgenInfo,
      name: "EuiSideNav",
      path: "src/components/side_nav/side_nav.tsx#EuiSideNav"
    };
} catch (__react_docgen_typescript_loader_error) {}